'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { fetchCategories } from '../../services/apiService';

// Map Help category IDs to actual category.en values from API
const categoryMapping = {
  'cold-flu': 'FluColds',
  'pain': 'PainFever',
  'sleep': 'RelaxationSleepMood',
  'skin': 'SkinHairNails',
  'stress-anxiety': 'RelaxationSleepMood'
};

/**
 * The Help categories and their navigation, shared by the standalone Help
 * section and the hero's "what's bothering you" strip so the two can never
 * disagree about which symptom opens which category.
 */
export const useHelpCategories = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [allCategories, setAllCategories] = useState([]);

  const categories = [
    {
      id: 'cold-flu',
      emoji: '🤧',
      title: t('home.help.categories.coldFlu.title'),
      description: t('home.help.categories.coldFlu.description'),
    },
    {
      id: 'pain',
      emoji: '😣',
      title: t('home.help.categories.pain.title'),
      description: t('home.help.categories.pain.description'),
    },
    {
      id: 'sleep',
      emoji: '😴',
      title: t('home.help.categories.sleep.title'),
      description: t('home.help.categories.sleep.description'),
    },
    {
      id: 'skin',
      emoji: '🌸',
      title: t('home.help.categories.skin.title'),
      description: t('home.help.categories.skin.description'),
    },
    {
      id: 'stress-anxiety',
      emoji: '🧠',
      title: t('home.help.categories.stressAnxiety.title'),
      description: t('home.help.categories.stressAnxiety.description'),
    }
  ];

  const fetchCategoriesData = useCallback(async () => {
    try {
      const response = await fetchCategories();
      setAllCategories(response.data || []);
    } catch (error) {
      setAllCategories([]);
    }
  }, []);

  useEffect(() => {
    fetchCategoriesData();
  }, [fetchCategoriesData]);

  const handleCategoryClick = (categoryId) => {
    const categoryEn = categoryMapping[categoryId];
    if (!categoryEn) {
      return;
    }

    // Find the actual category from the fetched categories
    const actualCategory = allCategories.find(
      cat => cat.category.en === categoryEn
    );

    if (actualCategory) {
      const cleanCategoryId = actualCategory.category.en.trim();
      sessionStorage.setItem('germanName', actualCategory.category.de);
      navigate(`/categories/${cleanCategoryId}/products`);
    } else {
      // Fallback: navigate using the mapped category name directly
      sessionStorage.setItem('germanName', categoryEn);
      navigate(`/categories/${categoryEn}/products`);
    }
  };

  return { categories, handleCategoryClick };
};

const Help = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { categories, handleCategoryClick } = useHelpCategories();

  return (
    <section 
      className="py-14 md:py-20 px-6 lg:px-8 w-full"
      style={{ backgroundColor: '#FFFFFF' }}
      aria-label="Health Help"
    >
      <div className="max-w-7xl mx-auto">
        <p className="mx-auto mb-12 max-w-2xl text-center text-[15px] leading-relaxed text-gray-500 md:text-xl">
          {t('home.help.subtitle')}
        </p>

        {/* Categories Grid */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-5 md:gap-6">
          {categories.map((category, index) => (
            <motion.button
              key={category.id}
              onClick={() => handleCategoryClick(category.id)}
              className="group relative rounded-[2rem] border border-gray-100 bg-white p-4 text-left sm:p-6 md:p-8 shadow-[0_18px_40px_-24px_rgba(17,24,39,0.35)] transition-[box-shadow,border-color] duration-300 hover:border-brand/15 hover:shadow-[0_30px_55px_-24px_rgb(var(--brand)/0.4)] focus:outline-none focus:ring-2 focus:ring-brand/25 focus:ring-offset-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              whileHover={{ y: -8, transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] } }}
              whileTap={{ scale: 0.985 }}
            >
              {/* Content */}
              <div className="relative z-10 flex flex-col items-center space-y-3 text-center sm:space-y-4">
                {/* Emoji */}
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-50 text-2xl transition-all duration-300 group-hover:bg-brand-tint group-hover:scale-110 sm:h-16 sm:w-16 sm:text-3xl md:text-4xl">
                  {category.emoji}
                </div>
                
                {/* Title */}
                <h3 className="text-sm font-bold tracking-[-0.02em] text-gray-900 transition-colors duration-300 group-hover:text-brand sm:text-lg md:text-xl">
                  {category.title}
                </h3>
                
                {/* Description */}
                <p className="hidden text-sm leading-relaxed text-gray-500 sm:block">
                  {category.description}
                </p>
              </div>

            </motion.button>
          ))}
        </div>

        {/* Optional: Subtle call-to-action */}
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <p className="flex flex-wrap items-center justify-center gap-3 text-sm md:text-base text-gray-500">
            {t('home.help.cta')}{' '}
            <button 
              onClick={() => navigate('/products')}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-brand/25 focus:ring-offset-2"
            >
              {t('home.help.browseAll')}
            </button>
          </p>
        </motion.div>
      </div>
    </section>
  );
}

Help.displayName = 'Help';

export default Help;

